import { useMemo } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { LoadingState } from '@/components/common/LoadingState/LoadingState';
import { Button } from '@/components/common/Button/Button';
import { PageBreadcrumb } from '@/components/layout/PageBreadcrumb/PageBreadcrumb';
import { StepHeader } from '@/components/layout/StepHeader/StepHeader';
import { CandidateProposalCard } from '@/components/positions/CandidateProposalCard/CandidateProposalCard';
import { PositionSummaryCard } from '@/components/positions/PositionSummaryCard/PositionSummaryCard';
import { usePositionsFlow } from '@/hooks/usePositionsFlow';
import { useSeo } from '@/hooks/useSeo';
import { buildPageTitle } from '@/lib/seo';
import { summarizeTopicPosition } from '@/utils/positionText';
import { buildCandidateProposalTopicPath } from '@/utils/candidateProposalRoute';
import { useCandidates } from '@/hooks/use-candidates';
import { useTopics } from '@/hooks/use-topics';
import { useTopicProposals } from '@/hooks/use-proposals';

import layoutStyles from '@/styles/layout.module.css';
import styles from '@/styles/positions.module.css';

export function TopicProposalsPage(): JSX.Element {
  const navigate = useNavigate();
  const params = useParams<{ topicId: string }>();
  const location = useLocation();
  const topicsState = useTopics();
  const candidatesState = useCandidates();
  const { questionPlan, answers } = usePositionsFlow();
  const topic =
    topicsState.status === 'success' && params.topicId
      ? topicsState.topics.find((entry) => entry.id === params.topicId)
      : null;

  const summary = useMemo(() => {
    if (!topic) {
      return '';
    }

    return summarizeTopicPosition(topic, questionPlan, answers);
  }, [answers, questionPlan, topic]);
  const isLegacyRoute = location.pathname.startsWith('/posicoes');

  useSeo({
    title: buildPageTitle(`${topic?.label ?? 'Tema'} | Propostas dos candidatos`),
    description: topic
      ? `Veja o que os candidatos propõem sobre ${topic.label} e compare as respostas com base em documentos oficiais.`
      : 'Veja o que os candidatos propõem e compare as respostas por tema.',
    canonicalPath: topic ? `/match-politico/tema/${topic.id}` : '/match-politico/resumo',
    indexable: !isLegacyRoute,
    ogType: 'article',
  });

  const proposalsState = useTopicProposals(topic?.id ?? null, candidatesState.candidates);

  if (topicsState.status === 'loading' || candidatesState.status === 'loading') {
    return (
      <section className={styles.questionCard}>
        <PageBreadcrumb items={[{ label: 'Match Político', to: '/match-politico' }, { label: 'Resultado', to: '/match-politico/resumo' }, { label: 'Tema' }]} />

        <StepHeader kicker="Tema" title="Carregando propostas" description="Consultando candidatos e temas públicos." />
        <LoadingState title="Carregando propostas" messages={['Consultando dados públicos', 'Montando as fontes', 'Preparando as respostas']} />
      </section>
    );
  }

  if (!topic) {
    return <Navigate to="/match-politico/resumo" replace />;
  }

  if (topicsState.status === 'error' || candidatesState.status === 'error') {
    return (
      <section className={styles.questionCard}>
        <PageBreadcrumb items={[{ label: 'Match Político', to: '/match-politico' }, { label: 'Resultado', to: '/match-politico/resumo' }, { label: 'Tema' }]} />

        <StepHeader kicker="Tema" title="Erro ao carregar dados" description="Não foi possível consultar os dados públicos agora." />
        <EmptyState
          tone="danger"
          title="Não foi possível carregar as propostas"
          description={topicsState.error ?? candidatesState.error ?? 'Tente novamente em instantes.'}
        />
      </section>
    );
  }

  if (proposalsState.status === 'loading') {
    return (
      <section className={styles.questionCard}>
        <PageBreadcrumb items={[{ label: 'Match Político', to: '/match-politico' }, { label: 'Resultado', to: '/match-politico/resumo' }, { label: topic.label }]} />

        <StepHeader
          kicker="Tema"
          title={topic.label}
          description="O que os candidatos propõem sobre este tema."
          actions={
            <Button type="button" variant="secondary" onClick={() => navigate('/match-politico/resumo')}>
              Voltar
            </Button>
          }
        />

        <LoadingState title="Carregando propostas" messages={['Consultando propostas públicas', 'Organizando fontes', 'Preparando os cards']} />
      </section>
    );
  }

  if (proposalsState.status === 'error') {
    return (
      <section className={styles.questionCard}>
        <PageBreadcrumb items={[{ label: 'Match Político', to: '/match-politico' }, { label: 'Resultado', to: '/match-politico/resumo' }, { label: topic.label }]} />

        <StepHeader
          kicker="Tema"
          title={topic.label}
          description="O que os candidatos propõem sobre este tema."
          actions={
            <Button type="button" variant="secondary" onClick={() => navigate('/match-politico/resumo')}>
              Voltar
            </Button>
          }
        />

        <EmptyState tone="danger" title="Não foi possível carregar as propostas" description={proposalsState.error ?? ''} />
      </section>
    );
  }

  return (
    <section className={styles.questionCard}>
      <PageBreadcrumb items={[{ label: 'Match Político', to: '/match-politico' }, { label: 'Resultado', to: '/match-politico/resumo' }, { label: topic.label }]} />

      <StepHeader
        kicker="Tema"
        title={topic.label}
        description="O que os candidatos propõem sobre este tema."
        actions={
          <Button type="button" variant="secondary" onClick={() => navigate('/match-politico/resumo')}>
            Voltar
          </Button>
        }
      />

      <PositionSummaryCard topicLabel={topic.label} summary={summary} />

      <section className={styles.questionCard} aria-label="Propostas dos candidatos">
        <div>
          <p className={layoutStyles.sectionTitle}>O que os candidatos propõem</p>
          <p className={layoutStyles.sectionSubtitle}>Todos os candidatos aparecem em ordem alfabética.</p>
        </div>

        <div className={styles.proposalGrid}>
          {proposalsState.proposals.map(({ candidate, proposal }) => (
            <CandidateProposalCard
              key={candidate.id}
              candidate={candidate}
              proposal={proposal}
              href={buildCandidateProposalTopicPath(candidate.id, topic.label)}
              secondaryHref={`/match-politico/tema/${topic.id}/candidato/${candidate.id}`}
              secondaryLabel="Ver fontes do tema"
            />
          ))}
        </div>
      </section>
    </section>
  );
}
